/**
 * Calculate discounted price based on a discount string and original price
 * @param discountString - Discount format like "20%", "20% off", "$10", "$10 off"
 * @param originalPriceString - Original price like "$109.99" or "109.99"
 * @returns Formatted discounted price like "$87.99"
 */
export const calculateDiscountedPrice = (discountString: string, originalPriceString: string): string => {
    // Parse original price - remove $ and convert to number
    const priceMatch = originalPriceString.match(/[\d.]+/);
    if (!priceMatch) {
        console.warn('Invalid price format:', originalPriceString);
        return originalPriceString; // Return original if parsing fails
    }
    const originalPrice = parseFloat(priceMatch[0]);

    if (isNaN(originalPrice) || originalPrice < 0) {
        console.warn('Invalid price value:', originalPrice);
        return originalPriceString;
    }

    // Parse discount string
    const discountLower = discountString.toLowerCase().trim();
    let discountedPrice = originalPrice;

    // Check for percentage discount
    if (discountLower.includes('%')) {
        const percentMatch = discountString.match(/[\d.]+/);
        if (percentMatch) {
            const percentage = parseFloat(percentMatch[0]);
            if (!isNaN(percentage) && percentage >= 0 && percentage <= 100) {
                discountedPrice = originalPrice * (1 - percentage / 100);
            } else {
                console.warn('Invalid percentage value:', percentage);
                return originalPriceString;
            }
        }
    }
    // Check for fixed amount discount
    else if (discountLower.includes('$')) {
        const amountMatch = discountString.match(/[\d.]+/);
        if (amountMatch) {
            const amount = parseFloat(amountMatch[0]);
            if (!isNaN(amount) && amount >= 0) {
                discountedPrice = originalPrice - amount;
            } else {
                console.warn('Invalid discount amount:', amount);
                return originalPriceString;
            }
        }
    } else {
        // Unknown discount format, return original
        console.warn('Unknown discount format:', discountString);
        return originalPriceString;
    }

    // Ensure price doesn't go below 0
    discountedPrice = Math.max(0, discountedPrice);

    // Format as currency with 2 decimal places
    return `$${discountedPrice.toFixed(2)}`;
};

/**
 * Apply discount to a plan's pricing information
 * @param plan - Plan object with amount and price properties
 * @param discount - Discount object with discount string
 * @returns Updated plan object with discounted pricing
 */
export const applyDiscountToPlan = (plan: any, discount: any) => {
    if (!discount || !discount.discount) {
        return plan;
    }

    const discountedAmount = calculateDiscountedPrice(discount.discount, plan.amount);
    const discountedPrice = calculateDiscountedPrice(discount.discount, plan.price);

    return {
        ...plan,
        amount: discountedAmount,
        price: discountedPrice,
        originalAmount: plan.amount,
        originalPrice: plan.price,
        hasDiscount: true
    };
};
